import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { DUR, EASE_OUT, useMotionOK } from '../motion';

export function Dialog({
    open,
    onClose,
    title,
    description,
    children,
    footer,
    width = 460,
    labelledBy,
}: {
    open: boolean;
    onClose: () => void;
    title?: string;
    description?: React.ReactNode;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    width?: number;
    labelledBy?: string;
}) {
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        const opener = document.activeElement as HTMLElement | null;
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            opener?.focus?.();
        };
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <Panel
                    onClose={onClose}
                    title={title}
                    description={description}
                    footer={footer}
                    width={width}
                    labelledBy={labelledBy}
                >
                    {children}
                </Panel>
            )}
        </AnimatePresence>
    );
}

/* Split out so the scrim and the card can animate on the way out as well as in;
   AnimatePresence needs the subtree to unmount, not the props to flip. */
function Panel({
    onClose,
    title,
    description,
    children,
    footer,
    width,
    labelledBy,
}: {
    onClose: () => void;
    title?: string;
    description?: React.ReactNode;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    width: number;
    labelledBy?: string;
}) {
    const motionOK = useMotionOK();
    const timing = { duration: motionOK ? DUR.base : 0, ease: EASE_OUT };

    return (
        <motion.div
            className="ds-dialog__scrim"
            onMouseDown={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={timing}
        >
            <motion.div
                initial={{ opacity: 0, scale: motionOK ? 0.96 : 1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: motionOK ? 0.97 : 1 }}
                transition={timing}
                className="ds-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelledBy}
                style={{ width }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {title && (
                    <div className={['ds-dialog__head', description ? '' : 'ds-dialog__head--lone'].filter(Boolean).join(' ')}>
                        <h3 className="ds-dialog__title" id={labelledBy}>
                            {title}
                        </h3>
                        <button className="ds-dialog__close" type="button" onClick={onClose} aria-label="Close">
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                                <path d="M4 4l8 8M12 4l-8 8" />
                            </svg>
                        </button>
                    </div>
                )}
                {description && <p className="ds-dialog__desc">{description}</p>}
                {children}
                {footer && <div className="ds-dialog__foot">{footer}</div>}
            </motion.div>
        </motion.div>
    );
}
